/**
 * WebSocket Context - Session-scoped WebSocket provider.
 *
 * Provides a singleton WebSocket connection that survives component
 * re-renders and page navigation. The connection is tied to the
 * user session, not the component lifecycle.
 *
 * Updated to support multi-agent orchestrator (specs/021-multi-agent-orchestrator).
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { WebSocketTransport } from '../transport/WebSocketTransport';
import type { TransportConnectionState } from '../transport/ChatTransport';
import type { RpcRequest, RpcNotification, RpcResponse } from '../rpc/types';
import { createBrowserTools } from '../rpc/BrowserTools';
import type { BrowserRpcMethods } from '../rpc/BrowserRpcMethods';
import { useAgentOptional } from './AgentContext';

const ORCHESTRATOR_URL =
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  process.env.REACT_APP_AGENT_URL ||
  'http://localhost:8090';

/** Spec 048/089: debounce window for the tool-lifecycle outbox (parity with
 * cxs2 ChatClient). */
const TOOL_OUTBOX_DEBOUNCE_MS = 50;
const PROTOCOL_VERSION = '1.0.0';
const CLIENT_VERSION = `basic-ui/${process.env.REACT_APP_VERSION || 'dev'}`;

type LifecycleOp =
  | { op: 'register'; tool: import('../rpc/types').BrowserToolDefinition }
  | { op: 'update'; tool: import('../rpc/types').BrowserToolDefinition }
  | { op: 'unregister'; name: string };

/** Same as useChatWithWebSocket: single agent uses /ws/{session}, not /agents/{slug}/ws/... */
const SINGLE_AGENT_MODE =
  process.env.REACT_APP_SINGLE_AGENT_SSE === 'true' ||
  process.env.REACT_APP_SINGLE_AGENT_SSE === '1';

/** Pending RPC request tracker */
interface PendingRpcRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export interface WebSocketContextValue {
  /** The WebSocket transport instance (singleton per session) */
  transport: WebSocketTransport | null;
  /** Current connection state */
  connectionState: TransportConnectionState;
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Browser tools instance for RPC */
  browserTools: BrowserRpcMethods | null;
  /** Connect to WebSocket for a session */
  connect: (sessionId: string, userId: string, organizationId?: string) => Promise<void>;
  /** Disconnect WebSocket */
  disconnect: () => void;
  /** Current session ID (if connected) */
  sessionId: string | null;
  /** Notify server that browser tools have changed */
  notifyToolsChanged: () => void;
  /** Register a custom browser tool and notify server */
  registerTool: (
    name: string,
    handler: (params: Record<string, unknown>) => Promise<unknown>,
    options?: {
      description?: string;
      parameters?: Record<string, unknown>;
      requiresPermission?: string[];
      fireAndForget?: boolean;
    },
  ) => void;
  /** Unregister a browser tool and notify server */
  unregisterTool: (name: string) => void;
  /** Send an RPC request and wait for response */
  sendRpcRequest: (
    method: string,
    params: Record<string, unknown>,
    timeout?: number,
  ) => Promise<unknown>;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export interface WebSocketProviderProps {
  children: React.ReactNode;
}

/**
 * WebSocket Provider - manages session-scoped WebSocket connection.
 *
 * The WebSocket connection is created once per session and survives
 * component re-renders and page navigation within the SPA.
 *
 * When an agent is selected via AgentContext, the WebSocket connects
 * to /agents/{slug}/ws/{session_id} (or /ws/{session_id} when
 * REACT_APP_SINGLE_AGENT_SSE is true).
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [connectionState, setConnectionState] =
    useState<TransportConnectionState>('disconnected');
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Get selected agent from AgentContext (optional - may not be available).
  // routingSlug is the URL slug — for Mystique it embeds the persona
  // (`mystique_{persona}`), so changing the persona reconnects the socket.
  const agentContext = useAgentOptional();
  const selectedAgentSlug = agentContext?.routingSlug ?? undefined;

  // Use refs to maintain stable references across renders
  const transportRef = useRef<WebSocketTransport | null>(null);
  const browserToolsRef = useRef<BrowserRpcMethods | null>(null);
  const currentSessionRef = useRef<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const currentOrganizationIdRef = useRef<string | undefined>(undefined);
  const currentAgentRef = useRef<string | null>(null);
  const pendingRpcRequestsRef = useRef<Map<string, PendingRpcRequest>>(new Map());
  const rpcIdCounterRef = useRef(0);

  // Spec 048/089 delta protocol: server capability set + debounced outbox.
  const serverSupportsRef = useRef<Set<string>>(new Set());
  const outboxRef = useRef<Map<string, LifecycleOp>>(new Map());
  const outboxOrderRef = useRef<string[]>([]);
  const outboxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create browser tools once (stable reference)
  useEffect(() => {
    browserToolsRef.current = createBrowserTools();
    // Dev-only: expose the BrowserRpcMethods instance for E2E tests and
    // manual debugging (e.g. registerTool from the devtools console). CRA
    // sets NODE_ENV at build time and dead-strips this branch in production.
    const isDev = process.env.NODE_ENV !== 'production';
    if (isDev && typeof window !== 'undefined') {
      (window as unknown as { __cxsBrowserTools?: BrowserRpcMethods }).__cxsBrowserTools =
        browserToolsRef.current;
    }
    return () => {
      browserToolsRef.current = null;
      if (isDev && typeof window !== 'undefined') {
        delete (window as unknown as { __cxsBrowserTools?: BrowserRpcMethods }).__cxsBrowserTools;
        delete (window as unknown as { __cxsNotifyToolsChanged?: () => void })
          .__cxsNotifyToolsChanged;
      }
    };
  }, []);

  // Handle RPC requests from the server
  const handleRpcRequest = useCallback(
    (request: RpcRequest, sendResponse: (response: RpcResponse) => void) => {
      const browserTools = browserToolsRef.current;
      if (!browserTools) {
        sendResponse({
          type: 'rpc-response',
          jsonrpc: '2.0',
          id: request.id,
          error: { code: -32603, message: 'Browser tools not initialized' },
        });
        return;
      }

      (async () => {
        try {
          let result: unknown;

          if (request.method === 'getToolDefinitions') {
            const meta = browserTools.getGroupMeta();
            result = {
              tools: browserTools.getToolDefinitions(),
              ...(meta.description ? { groupDescription: meta.description } : {}),
              ...(meta.notes ? { groupNotes: meta.notes } : {}),
            };
          } else if (request.method === 'executeTool') {
            // Support both agent format (toolName/args) and standard format (name/params)
            const params = request.params || {};
            const toolName = (params.toolName || params.name) as string;
            const toolParams = (params.args || params.params || {}) as Record<
              string,
              unknown
            >;
            result = await browserTools.executeTool(toolName, toolParams);
          } else {
            // Direct tool execution
            result = await browserTools.executeTool(
              request.method,
              request.params || {},
            );
          }

          sendResponse({
            type: 'rpc-response',
            jsonrpc: '2.0',
            id: request.id,
            result,
          });
        } catch (error) {
          const rpcError = error as {
            code?: number;
            message?: string;
            data?: unknown;
          };
          sendResponse({
            type: 'rpc-response',
            jsonrpc: '2.0',
            id: request.id,
            error: {
              code: rpcError.code ?? -32603,
              message: rpcError.message ?? String(error),
              data: rpcError.data,
            },
          });
        }
      })();
    },
    [],
  );

  // Handle RPC notifications from the server (fire-and-forget)
  const handleRpcNotification = useCallback((notification: RpcNotification) => {
    // Generic agent push — any backend component can push assistant messages
    if (notification.method === 'agentPush') {
      window.dispatchEvent(
        new CustomEvent('cxs-agent-push', { detail: notification.params || {} }),
      );
      return;
    }

    const browserTools = browserToolsRef.current;
    if (!browserTools) {
      console.warn('Browser tools not initialized for notification');
      return;
    }

    (async () => {
      try {
        await browserTools.executeTool(
          notification.method,
          notification.params || {},
        );
      } catch (error) {
        console.warn(`RPC notification error for ${notification.method}:`, error);
      }
    })();
  }, []);

  // Handle RPC responses from the server (for outbound RPC calls)
  const handleRpcResponse = useCallback((response: RpcResponse) => {
    const pending = pendingRpcRequestsRef.current.get(response.id);
    if (!pending) {
      console.warn(`Received RPC response for unknown request ID: ${response.id}`);
      return;
    }

    // Clean up
    clearTimeout(pending.timeoutId);
    pendingRpcRequestsRef.current.delete(response.id);

    // Resolve or reject based on response
    if (response.error) {
      pending.reject(new Error(response.error.message || 'RPC error'));
    } else {
      pending.resolve(response.result);
    }
  }, []);

  // Spec 048/089: outbox flush — delta frames when the server understands
  // them, legacy full-list otherwise.
  const flushOutbox = useCallback(() => {
    const transport = transportRef.current;
    const browserTools = browserToolsRef.current;
    if (!transport?.isConnected || !browserTools || outboxRef.current.size === 0) {
      return;
    }
    const ops = outboxOrderRef.current
      .map((k) => outboxRef.current.get(k))
      .filter((op): op is LifecycleOp => op !== undefined);
    outboxRef.current.clear();
    outboxOrderRef.current = [];

    if (serverSupportsRef.current.has('tools-delta')) {
      const added: unknown[] = [];
      const removed: string[] = [];
      const updated: unknown[] = [];
      for (const op of ops) {
        if (op.op === 'register') added.push(op.tool);
        else if (op.op === 'update') updated.push(op.tool);
        else removed.push(op.name);
      }
      const payload: Record<string, unknown> = { type: 'tools-delta' };
      if (added.length > 0) payload.added = added;
      if (removed.length > 0) payload.removed = removed;
      if (updated.length > 0) payload.updated = updated;
      transport.send(payload);
      console.info(`browser_tool_lifecycle transport=delta ops=${ops.length}`);
    } else {
      const meta = browserTools.getGroupMeta();
      transport.send({
        type: 'tools-changed',
        tools: browserTools.getToolDefinitions(),
        ...(meta.description ? { groupDescription: meta.description } : {}),
        ...(meta.notes ? { groupNotes: meta.notes } : {}),
      });
      console.info(`browser_tool_lifecycle transport=full_list ops=${ops.length}`);
    }
  }, []);

  const scheduleFlush = useCallback(
    (delayMs: number) => {
      if (outboxTimerRef.current) {
        clearTimeout(outboxTimerRef.current);
      }
      outboxTimerRef.current = setTimeout(() => {
        outboxTimerRef.current = null;
        flushOutbox();
      }, delayMs);
    },
    [flushOutbox],
  );

  const enqueueOp = useCallback(
    (op: LifecycleOp) => {
      const key = op.op === 'unregister' ? op.name : op.tool.name;
      if (!outboxRef.current.has(key)) {
        outboxOrderRef.current.push(key);
      }
      outboxRef.current.set(key, op);
      scheduleFlush(TOOL_OUTBOX_DEBOUNCE_MS);
    },
    [scheduleFlush],
  );

  const handleServerCapabilities = useCallback(
    (supports: string[]) => {
      serverSupportsRef.current = new Set(supports);
      scheduleFlush(0);
    },
    [scheduleFlush],
  );

  // Handle connection state changes
  const handleConnectionStateChange = useCallback(
    (state: TransportConnectionState) => {
      setConnectionState(state);
      if (state === 'connected') {
        // Spec 048/089 handshake: advertise our capability set, protocol
        // version, and client version (the frontend_version telemetry
        // dimension) as the first frame after connect.
        transportRef.current?.send({
          type: 'capabilities',
          supports: [
            'tool-register',
            'tool-unregister',
            'tool-update',
            'tools-delta',
            'capabilities',
          ],
          protocolVersion: PROTOCOL_VERSION,
          clientVersion: CLIENT_VERSION,
        });
        scheduleFlush(0);
      }
    },
    [scheduleFlush],
  );

  // Connect to WebSocket
  const connect = useCallback(
    async (newSessionId: string, userId: string, organizationId?: string) => {
      const agentSlug = selectedAgentSlug || 'dex';
      const routingKey = SINGLE_AGENT_MODE ? '__single__' : agentSlug;
      const wsEndpoint = SINGLE_AGENT_MODE ? '/ws' : `/agents/${agentSlug}/ws`;

      // If already connected to the same session AND same agent, skip.
      // Use transport's isConnected (checks ws.readyState directly) instead
      // of React state to avoid cascading re-renders causing reconnection storms.
      if (
        currentSessionRef.current === newSessionId &&
        currentAgentRef.current === routingKey &&
        transportRef.current?.isConnected
      ) {
        return;
      }

      // If connected to a different session or agent, disconnect first
      if (
        (currentSessionRef.current && currentSessionRef.current !== newSessionId) ||
        (currentAgentRef.current && currentAgentRef.current !== routingKey)
      ) {
        transportRef.current?.dispose();
        transportRef.current = null;
      }

      // Create new transport if needed
      if (!transportRef.current) {
        transportRef.current = new WebSocketTransport({
          baseUrl: ORCHESTRATOR_URL,
          wsEndpoint,
          onConnectionStateChange: handleConnectionStateChange,
          onRpcRequest: handleRpcRequest,
          onRpcNotification: handleRpcNotification,
          onRpcResponse: handleRpcResponse,
          onServerCapabilities: handleServerCapabilities,
        });
      }

      currentSessionRef.current = newSessionId;
      currentUserIdRef.current = userId;
      currentOrganizationIdRef.current = organizationId;
      currentAgentRef.current = routingKey;
      setSessionId(newSessionId);

      // Connect
      await transportRef.current.connect(newSessionId, userId, organizationId);
    },
    [
      selectedAgentSlug,
      handleConnectionStateChange,
      handleRpcRequest,
      handleRpcNotification,
      handleRpcResponse,
      handleServerCapabilities,
    ],
  );

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    transportRef.current?.dispose();
    transportRef.current = null;
    currentSessionRef.current = null;
    setSessionId(null);
    setConnectionState('disconnected');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      transportRef.current?.dispose();
    };
  }, []);

  // Keepalive: periodically reconnect if WebSocket dropped while idle.
  // This ensures push notifications (e.g., kdense status) can still arrive.
  const connectRef = useRef(connect);
  connectRef.current = connect;
  useEffect(() => {
    const interval = setInterval(() => {
      const transport = transportRef.current;
      const session = currentSessionRef.current;
      if (session && transport && !transport.isConnected) {
        const uid = currentUserIdRef.current || 'default';
        const orgId = currentOrganizationIdRef.current;
        transport.connect(session, uid, orgId).catch(() => {
          // Silently ignore — next interval will retry
        });
      }
    }, 30_000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Reconnect when agent changes (if already connected)
  useEffect(() => {
    if (SINGLE_AGENT_MODE) {
      return;
    }
    if (
      currentSessionRef.current &&
      selectedAgentSlug &&
      currentAgentRef.current !== selectedAgentSlug &&
      connectionState === 'connected'
    ) {
      // Agent changed while connected, reconnect to new agent's WebSocket
      const sessionId = currentSessionRef.current;
      transportRef.current?.dispose();
      transportRef.current = null;

      // Create new transport for the new agent
      transportRef.current = new WebSocketTransport({
        baseUrl: ORCHESTRATOR_URL,
        wsEndpoint: `/agents/${selectedAgentSlug}/ws`,
        onConnectionStateChange: handleConnectionStateChange,
        onRpcRequest: handleRpcRequest,
        onRpcNotification: handleRpcNotification,
        onRpcResponse: handleRpcResponse,
        onServerCapabilities: handleServerCapabilities,
      });

      currentAgentRef.current = selectedAgentSlug;

      // Reconnect with the stored userId and organizationId from the original connection
      const uid = currentUserIdRef.current || 'default';
      const orgId = currentOrganizationIdRef.current;
      transportRef.current.connect(sessionId, uid, orgId).catch((err) => {
        console.error('Failed to reconnect WebSocket after agent change:', err);
      });
    }
  }, [
    selectedAgentSlug,
    connectionState,
    handleConnectionStateChange,
    handleRpcRequest,
    handleRpcNotification,
    handleRpcResponse,
  ]);

  // Notify server that browser tools have changed
  const notifyToolsChanged = useCallback(() => {
    const transport = transportRef.current;
    const browserTools = browserToolsRef.current;
    if (!transport || !browserTools) {
      return;
    }

    const tools = browserTools.getToolDefinitions();
    const meta = browserTools.getGroupMeta();
    transport.send({
      type: 'tools-changed',
      tools,
      ...(meta.description ? { groupDescription: meta.description } : {}),
      ...(meta.notes ? { groupNotes: meta.notes } : {}),
    });
  }, []);

  // Dev-only: expose the notify helper for E2E tests that manipulate the
  // tool registry via __cxsBrowserTools directly (spec 048 roundtrip demo).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
      return;
    }
    (window as unknown as { __cxsNotifyToolsChanged?: () => void })
      .__cxsNotifyToolsChanged = notifyToolsChanged;
  }, [notifyToolsChanged]);

  // Register a custom browser tool and notify server
  const registerTool = useCallback(
    (
      name: string,
      handler: (params: Record<string, unknown>) => Promise<unknown>,
      options?: {
        description?: string;
        parameters?: Record<string, unknown>;
        requiresPermission?: string[];
        fireAndForget?: boolean;
      },
    ) => {
      const browserTools = browserToolsRef.current;
      if (!browserTools) {
        console.warn('Browser tools not initialized');
        return;
      }

      const alreadyRegistered = browserTools
        .getToolDefinitions()
        .some((tool) => tool.name === name);
      browserTools.registerTool({
        name,
        handler,
        description: options?.description ?? `Custom tool: ${name}`,
        parameters: options?.parameters ?? {},
        requiresPermission: options?.requiresPermission,
        fireAndForget: options?.fireAndForget ?? false,
      });

      const definition = browserTools
        .getToolDefinitions()
        .find((tool) => tool.name === name);
      if (definition) {
        enqueueOp(
          alreadyRegistered
            ? { op: 'update', tool: definition }
            : { op: 'register', tool: definition },
        );
      }
    },
    [enqueueOp],
  );

  // Unregister a browser tool and notify server
  const unregisterTool = useCallback(
    (name: string) => {
      const browserTools = browserToolsRef.current;
      if (!browserTools) {
        return;
      }

      browserTools.unregisterTool(name);

      enqueueOp({ op: 'unregister', name });
    },
    [enqueueOp],
  );

  // Send an RPC request and wait for response
  const sendRpcRequest = useCallback(
    async (
      method: string,
      params: Record<string, unknown>,
      timeout = 30000,
    ): Promise<unknown> => {
      const transport = transportRef.current;
      if (!transport) {
        throw new Error('WebSocket not connected');
      }

      const requestId = `rpc-${Date.now()}-${++rpcIdCounterRef.current}`;

      return new Promise((resolve, reject) => {
        // Set up timeout
        const timeoutId = setTimeout(() => {
          pendingRpcRequestsRef.current.delete(requestId);
          reject(new Error('RPC request timed out'));
        }, timeout);

        // Track pending request
        pendingRpcRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        // Send request
        try {
          transport.send({
            type: 'rpc-request',
            jsonrpc: '2.0',
            id: requestId,
            method,
            params,
          });
        } catch (error) {
          clearTimeout(timeoutId);
          pendingRpcRequestsRef.current.delete(requestId);
          reject(error);
        }
      });
    },
    [],
  );

  const value = useMemo<WebSocketContextValue>(
    () => ({
      transport: transportRef.current,
      connectionState,
      isConnected: connectionState === 'connected',
      browserTools: browserToolsRef.current,
      connect,
      disconnect,
      sessionId,
      notifyToolsChanged,
      registerTool,
      unregisterTool,
      sendRpcRequest,
    }),
    [
      connectionState,
      connect,
      disconnect,
      sessionId,
      notifyToolsChanged,
      registerTool,
      unregisterTool,
      sendRpcRequest,
    ],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context.
 *
 * Must be used within a WebSocketProvider.
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to access WebSocket context (optional).
 *
 * Returns null if not within a WebSocketProvider.
 */
export function useWebSocketOptional(): WebSocketContextValue | null {
  return useContext(WebSocketContext);
}
